import React, { useEffect, useImperativeHandle, useState } from "react";
import {
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { normalizeTimestamp } from "../../helpers/utils";

const StatusCard = React.forwardRef(
  ({ card, topic, messages, w, h, displayVariant, displayParameters }, ref) => {
    // Use messages if available; otherwise, set to an empty array.
    const safeMessages = messages || [];
    const lastMessage = safeMessages.length > 0 ? safeMessages[0] : null;

    // Expose an action via the ref.
    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    // State to trigger re-renders for dynamic time updates.
    const [currentTime, setCurrentTime] = useState(Date.now());
    useEffect(() => {
      const intervalId = setInterval(() => {
        setCurrentTime(Date.now());
      }, 60000); // Update every 60 seconds
      return () => clearInterval(intervalId);
    }, []);

    // Calculate time display
    let messageTime,
      timeText = "No messages";
    let totalMinutes = 0;
    if (lastMessage && lastMessage.timestamp) {
      messageTime = normalizeTimestamp(lastMessage.timestamp);
      const timeDifference = currentTime - messageTime.getTime();
      totalMinutes = Math.floor(timeDifference / (1000 * 60));

      if (totalMinutes < 1) {
        timeText = "less than 1 minute ago";
      } else if (totalMinutes >= 60) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        timeText = `${hours} ${hours === 1 ? "hour" : "hours"} ${
          minutes > 0
            ? `${minutes} ${minutes === 1 ? "minute" : "minutes"}`
            : ""
        } ago`;
      } else {
        timeText = `${totalMinutes} ${
          totalMinutes === 1 ? "minute" : "minutes"
        } ago`;
      }
    }

    // Determine the card size key (used to choose rendering style)
    const sizeKey = `${w}x${h}`;

    // Helper function to determine status configuration based on totalMinutes.
    function getStatusConfig(totalMinutes) {
      if (totalMinutes >= 120) {
        return {
          cardColor: "bg-gray-50",
          icon: faTimesCircle,
          statusText: "UNRESPONSIVE",
          iconColor: "text-burnt-sienna",
        };
      } else if (totalMinutes >= 30) {
        return {
          cardColor: "bg-gray-50",
          icon: faExclamationTriangle,
          statusText: "WAITING",
          iconColor: "text-sunset",
        };
      } else {
        return {
          cardColor: "bg-gray-50",
          icon: faCheckCircle,
          statusText: "ALIVE",
          iconColor: "text-cambridge-blue",
        };
      }
    }
    const statusConfig = getStatusConfig(totalMinutes);

    // Define render functions based on size.
    const renderContent = () => {
      // If no last message and display is auto, fallback to a simple TopicCard.
      if (!lastMessage && statusConfig.statusText === "ALIVE") {
        return (
          <div className="flex flex-col justify-center items-center gap-2 w-full h-full mt-1">
            <h2 className="text-sm font-semibold text-delft-blue">
              {topic.name}
            </h2>
            <p className="text-xs text-gray-500">No messages</p>
          </div>
        );
      }

      switch (sizeKey) {
        case "1x1":
          return (
            <div className="flex flex-col justify-center items-center gap-2 w-full h-full mt-1">
              {statusConfig.icon && (
                <FontAwesomeIcon
                  icon={statusConfig.icon}
                  className={`${statusConfig.iconColor}`}
                  size="2x"
                />
              )}
              <h2 className="text-sm font-semibold text-delft-blue wrap">
                {topic.name}
              </h2>
            </div>
          );
        case "1x2":
          return (
            <div className="flex flex-col items-center justify-center w-full h-full">
              {statusConfig.icon && (
                <FontAwesomeIcon
                  icon={statusConfig.icon}
                  className={`${statusConfig.iconColor} text-3xl mb-2`}
                />
              )}
              <p className="text-sm font-semibold text-gray-700">
                {statusConfig.statusText}
              </p>
            </div>
          );
        case "2x1":
          return (
            <div className="flex items-center justify-center w-full h-full">
              {statusConfig.icon && (
                <FontAwesomeIcon
                  icon={statusConfig.icon}
                  className={statusConfig.iconColor}
                  size="2x"
                />
              )}
              <p className="ml-2 text-sm font-semibold text-delft-blue">
                {topic.name}
              </p>
            </div>
          );
        // Add other cases (1x3, 3x1, 2x2, etc.) similarly.
        default:
          return (
            <div className="flex flex-col justify-center items-center w-full h-full">
              <h2 className="text-sm font-semibold mb-2 text-delft-blue">
                {topic.name}
              </h2>
              <div className="flex space-x-1">
                {statusConfig.icon && (
                  <FontAwesomeIcon
                    icon={statusConfig.icon}
                    className={statusConfig.iconColor}
                    size="xl"
                  />
                )}
                <p className="text-sm font-semibold text-delft-blue">
                  {statusConfig.statusText}
                </p>
              </div>
              <p className="text-xs text-delft-blue">{timeText}</p>
            </div>
          );
      }
    };

    // Decide which render function to use based on displayVariant.
    const renderFunction = ["default", "", undefined].includes(displayVariant)
      ? renderContent
      : () => (
          <div className="flex flex-col justify-between w-full h-full">
            <h2 className="text-lg font-semibold text-delft-blue">
              {topic.name}
            </h2>
            <p
              className={`text-[3rem] font-semibold text-delft-blue text-center my-auto ${
                displayParameters?.blah ? "-rotate-90" : ""
              }`}
            >
              {safeMessages.length}
            </p>
          </div>
        );

    return (
      <div
        className={`p-2 shadow-md cursor-pointer transition-transform duration-300 ${statusConfig.cardColor} w-full h-full`}
      >
        {renderFunction()}
      </div>
    );
  }
);

StatusCard.variants = ["default", "count"];
StatusCard.variantParameters = {
  default: {},
  count: {
    blah: {
      label: "Rotate the text 90 degrees to the left",
      type: "bool",
      default: true,
    },
    optionsin: {
      label: "Input options test",
      type: "option",
      options: ["default", "option1", "option2", "option3", "option4"],
      default: "default",
    },
    textin: {
      label: "Input text test",
      type: "string",
      default: null,
    },
    intin: {
      label: "Input integer test",
      type: "int",
      default: null,
      min: 5,
      max: 18,
    },
  },
};

StatusCard.propTypes = {
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
};

export default StatusCard;
